import { Typography } from '@material-ui/core';
import classNames from 'classnames';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { LEVEL_PRESENTATION, levelState } from 'components/ApprovalLevelChain';

import ruleMessages from '../messages';
import messages from './messages';
import { useStyles } from './styles';
import { Level, RuleGroup } from './types';

interface ApprovalStatusCellProps {
  rules: RuleGroup[];
  /**
   * Whether the request itself has finished.
   *
   * Required by `levelState` rather than derivable from the levels: a cancelled
   * request leaves its open level reading `active`, so a cell that judged each
   * level on its own would say a withdrawn approval was still awaiting somebody.
   */
  requestSettled: boolean;
}

/**
 * How far one approval has got, level by level — the queue's Approval status
 * column (Design Requirements §5).
 *
 * ## The same answer as the panel, in a row's worth of height
 *
 * State comes from `levelState` and its wording and glyph from
 * `LEVEL_PRESENTATION`, both shared with the review panel's rail. What is not
 * shared is the layout: the rail lists every approver, their email, the role they
 * were resolved through and any comment they left, which is a column of its own
 * rather than a table cell. So the cell renders one line per level — the level's
 * name and what it has come to — and the panel behind the row carries the rest.
 *
 * ## Rule order, not position order
 *
 * `position` is 1-based within a rule, so two rules that both gate a subject each
 * have a level at position 1 (AXHE-4183). Sorting the flattened list by position
 * interleaves two independent ladders and reads as one. Keeping the server's
 * grouping order means the levels of the first rule are listed, then the second's
 * — the same order the Approver rule column beside this one lists the rules in,
 * so the two cells read across.
 */
export const ApprovalStatusCell: React.FC<ApprovalStatusCellProps> = ({
  rules,
  requestSettled,
}) => {
  const styles = useStyles();
  const intl = useIntl();
  const levels: Level[] = rules.flatMap(rule => rule.levels);

  if (levels.length === 0) {
    return (
      <Typography color="textSecondary" variant="body2">
        <FormattedMessage {...messages.noLevels} />
      </Typography>
    );
  }

  return (
    <div className={styles.statusCell} data-testid="approval-status">
      {levels.map(level => {
        const state = levelState(level, requestSettled);
        const { label, tone, Icon } = LEVEL_PRESENTATION[state];

        return (
          <div className={styles.statusLine} key={level.stepId}>
            {/* Decorative: the state's own word is printed beside it. */}
            <Icon
              aria-hidden="true"
              className={classNames(styles.statusIcon, {
                [styles.statusIconApproved]: tone === 'approved',
                [styles.statusIconRejected]: tone === 'rejected',
              })}
            />
            <Typography className={styles.statusLevel} variant="body2">
              {level.name ? (
                <FormattedMessage
                  {...ruleMessages.levelRowNamed}
                  values={{ number: level.position, name: level.name }}
                />
              ) : (
                <FormattedMessage
                  {...ruleMessages.levelRow}
                  values={{ number: level.position }}
                />
              )}
            </Typography>
            <Typography color="textSecondary" variant="body2">
              {intl.formatMessage(label)}
            </Typography>
            {/* An open level nobody can clear. Called out rather than left
                reading as merely waiting, because it is a misconfiguration
                somebody has to fix rather than a state anybody chose. */}
            {level.needsAttention && (
              <Typography className={styles.statusAttention} variant="body2">
                <FormattedMessage {...messages.levelNeedsAttention} />
              </Typography>
            )}
          </div>
        );
      })}
    </div>
  );
};
